/**
 * CatalogoPublico — Vista pública del catálogo (Mejora #9).
 *
 * Ruta: /c/:empresaId
 *
 * Página standalone (sin Layout admin, sin PrivateRoute). Pensada para
 * compartirse por WhatsApp/Instagram/etc. El cliente final ve:
 *   - Encabezado con nombre+logo de la empresa
 *   - Grid de paquetes activos con imagen principal, PVP, inclusiones
 *   - CTA "Reservar via WhatsApp" → wa.me/<numero>?text=<pre-fill paquete>
 *   - Botón compartir (Web Share API si está disponible, sino copia link)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardMedia, CardContent, Button, Chip, IconButton,
  CircularProgress, Dialog, DialogContent, DialogTitle, Stack,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function fmtMoney(n) {
  if (n == null) return '—';
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n));
}

export default function CatalogoPublico() {
  const { empresaId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);  // paquete abierto en modal
  const [shareToast, setShareToast] = useState('');

  useEffect(() => {
    document.title = 'Catálogo · NARA Tourism';
    fetch(`${API_BASE}/public/catalogo/${empresaId}`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(d => {
        setData(d);
        if (d.empresa?.nombre) document.title = `${d.empresa.nombre} · Catálogo`;
      })
      .catch(e => setError('No se pudo cargar el catálogo. Verificá el link.'))
      .finally(() => setLoading(false));
  }, [empresaId]);

  const handleReservar = (paquete) => {
    const numero = data?.whatsapp_numero?.replace(/[^\d+]/g, '');
    if (!numero) {
      setShareToast('No hay número de WhatsApp configurado');
      setTimeout(() => setShareToast(''), 3000);
      return;
    }
    const texto = `Hola! Me interesa el paquete *${paquete.nombre}*${paquete.destino_nombre ? ` (${paquete.destino_nombre})` : ''}. ¿Me podés dar más información para reservar?`;
    window.open(`https://wa.me/${numero.replace('+', '')}?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = `${data?.empresa?.nombre || 'Catálogo'} — Reservá tu pasadía`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) { /* usuario canceló */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShareToast('Link copiado al portapapeles');
        setTimeout(() => setShareToast(''), 2500);
      } catch (e) {
        setShareToast('No se pudo copiar');
        setTimeout(() => setShareToast(''), 2500);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#dc2626', mb: 1 }}>
          ⚠️ {error || 'Catálogo no encontrado'}
        </Typography>
      </Box>
    );
  }

  const { empresa, paquetes } = data;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f0f9ff 0%, #f8fafc 100%)',
      pb: 6,
    }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        color: '#fff',
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
        boxShadow: '0 4px 20px rgba(13, 148, 136, 0.3)',
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {empresa.logo_url && (
            <Box sx={{ width: 56, height: 56, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', p: 0.5 }}>
              <img src={empresa.logo_url} alt={empresa.nombre}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 800, lineHeight: 1.2 }}>
              {empresa.nombre}
            </Typography>
            <Typography sx={{ fontSize: { xs: 11, md: 13 }, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 14 }} />
              {empresa.ciudad}{empresa.pais ? `, ${empresa.pais}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={handleShare} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <ShareIcon />
          </IconButton>
        </Box>
        <Typography sx={{ maxWidth: 1200, mx: 'auto', mt: 2, fontSize: { xs: 14, md: 16 }, opacity: 0.95 }}>
          🏝️ Reservá tu pasadía o tour. Hacé click en cualquier paquete y te conectamos por WhatsApp.
        </Typography>
      </Box>

      {/* Toast de share */}
      {shareToast && (
        <Box sx={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          bgcolor: '#0f172a', color: '#fff', px: 2, py: 1, borderRadius: 2,
          fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        }}>
          {shareToast}
        </Box>
      )}

      {/* Grid de paquetes */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, mt: 3 }}>
        {paquetes.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', bgcolor: '#fff', borderRadius: 3 }}>
            <Typography sx={{ fontSize: 16, color: '#64748b' }}>
              No hay paquetes disponibles por el momento.
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          }}>
            {paquetes.map(p => {
              const imgUrl = p.imagen_principal_url
                ? (p.imagen_principal_url.startsWith('http') ? p.imagen_principal_url : `${API_BASE}${p.imagen_principal_url}`)
                : null;
              const inclusiones = (p.incluye || []).slice(0, 4);
              const inclusionesExtras = (p.incluye || []).length - inclusiones.length;
              return (
                <Card key={p.id}
                  onClick={() => setDetalle(p)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(13, 148, 136, 0.18)',
                    },
                  }}>
                  {imgUrl ? (
                    <CardMedia component="img" image={imgUrl} alt={p.nombre}
                      sx={{ height: 200, objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ height: 200, bgcolor: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 40 }}>🏖️</Typography>
                    </Box>
                  )}
                  <CardContent sx={{ p: 2 }}>
                    {p.destino_nombre && (
                      <Chip label={p.destino_nombre} size="small" icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                        sx={{ mb: 1, bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 600, fontSize: 11 }} />
                    )}
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a', mb: 1, lineHeight: 1.3 }}>
                      {p.nombre}
                    </Typography>
                    {/* Precio destacado */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0d9488' }}>
                        {fmtMoney(p.precio_adulto)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>por adulto</Typography>
                      {p.precio_nino != null && (
                        <Typography sx={{ fontSize: 11, color: '#64748b', ml: 'auto' }}>
                          Niño {fmtMoney(p.precio_nino)}
                        </Typography>
                      )}
                    </Box>
                    {/* Horarios */}
                    {(p.hora_salida || p.hora_regreso) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', mb: 1.5 }}>
                        <ScheduleIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11 }}>
                          {p.hora_salida} → {p.hora_regreso}
                        </Typography>
                        {p.capacidad_maxima && (
                          <>
                            <GroupIcon sx={{ fontSize: 14, ml: 1 }} />
                            <Typography sx={{ fontSize: 11 }}>Hasta {p.capacidad_maxima}</Typography>
                          </>
                        )}
                      </Box>
                    )}
                    {/* Top inclusiones */}
                    {inclusiones.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        {inclusiones.map((inc, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a', mt: 0.25, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>{inc}</Typography>
                          </Box>
                        ))}
                        {inclusionesExtras > 0 && (
                          <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 600, ml: 2.5 }}>
                            + {inclusionesExtras} más…
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Button fullWidth variant="contained" startIcon={<WhatsAppIcon />}
                      onClick={(e) => { e.stopPropagation(); handleReservar(p); }}
                      sx={{
                        textTransform: 'none', fontWeight: 700, mt: 1,
                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                        boxShadow: '0 4px 12px rgba(37, 211, 102, 0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)' },
                      }}>
                      Reservar por WhatsApp
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Modal de detalle del paquete */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} fullWidth maxWidth="md">
        {detalle && (
          <>
            <DialogTitle sx={{
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              color: '#fff', display: 'flex', alignItems: 'center', gap: 1, py: 1.5,
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{detalle.nombre}</Typography>
                {detalle.destino_nombre && (
                  <Typography sx={{ fontSize: 11, opacity: 0.9 }}>📍 {detalle.destino_nombre}</Typography>
                )}
              </Box>
              <IconButton onClick={() => setDetalle(null)} sx={{ color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* Galería */}
              {detalle.imagenes?.length > 0 ? (
                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, p: 1, bgcolor: '#f1f5f9' }}>
                  {detalle.imagenes.map((img, i) => (
                    <Box key={i} sx={{ flexShrink: 0, width: 220, height: 160, borderRadius: 1, overflow: 'hidden' }}>
                      <img src={img.url.startsWith('http') ? img.url : `${API_BASE}${img.url}`}
                        alt={img.descripcion || ''}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              ) : detalle.imagen_principal_url && (
                <Box sx={{ height: 280, overflow: 'hidden' }}>
                  <img src={detalle.imagen_principal_url.startsWith('http') ? detalle.imagen_principal_url : `${API_BASE}${detalle.imagen_principal_url}`}
                    alt={detalle.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              )}

              <Box sx={{ p: 3 }}>
                {/* Precios */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4', minWidth: 140 }}>
                    <Typography sx={{ fontSize: 10, color: '#0f766e', fontWeight: 700, textTransform: 'uppercase' }}>Adulto</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0d9488' }}>{fmtMoney(detalle.precio_adulto)}</Typography>
                  </Box>
                  {detalle.precio_nino != null && (
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd', minWidth: 140 }}>
                      <Typography sx={{ fontSize: 10, color: '#0c4a6e', fontWeight: 700, textTransform: 'uppercase' }}>Niño</Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0284c7' }}>{fmtMoney(detalle.precio_nino)}</Typography>
                    </Box>
                  )}
                  {(detalle.hora_salida || detalle.hora_regreso) && (
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', flex: 1, minWidth: 200 }}>
                      <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase' }}>Horario</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                        {detalle.hora_salida} → {detalle.hora_regreso}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Descripción */}
                {detalle.descripcion && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {detalle.descripcion}
                    </Typography>
                  </Box>
                )}

                {/* Inclusiones / exclusiones */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {detalle.incluye?.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#166534', mb: 1, textTransform: 'uppercase' }}>
                        ✅ Incluye
                      </Typography>
                      <Stack spacing={0.5}>
                        {detalle.incluye.map((inc, i) => (
                          <Typography key={i} sx={{ fontSize: 12, color: '#475569' }}>• {inc}</Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {detalle.no_incluye?.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#991b1b', mb: 1, textTransform: 'uppercase' }}>
                        ❌ No incluye
                      </Typography>
                      <Stack spacing={0.5}>
                        {detalle.no_incluye.map((noinc, i) => (
                          <Typography key={i} sx={{ fontSize: 12, color: '#475569' }}>• {noinc}</Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>

                {/* CTA al final */}
                <Button fullWidth variant="contained" size="large" startIcon={<WhatsAppIcon />}
                  onClick={() => handleReservar(detalle)}
                  sx={{
                    mt: 3, textTransform: 'none', fontWeight: 700, fontSize: 16, py: 1.5,
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    boxShadow: '0 4px 16px rgba(37, 211, 102, 0.4)',
                    '&:hover': { background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)' },
                  }}>
                  Reservar este paquete por WhatsApp
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 4, color: '#94a3b8', fontSize: 11 }}>
        Powered by <strong>NARA Tourism</strong>
      </Box>
    </Box>
  );
}
